import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Fingerprint, Smartphone, Trash2 } from 'lucide-react';
import { PRINCIPALE, contesto, useTraduzioni } from '../../i18n';
import { API, axios } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { formattaRoma } from '../../utils/percorso';
import {
  passkeySupportate, piattaforma, dispositivoConPasskey, dimenticaDispositivo,
  preparaRegistrazione, registraPasskey, annullato,
} from '../../utils/passkey';

// Date e ore nell'ora di Roma (il server le manda in UTC): prima nell'ora del telefono, quindi a New York "alle 04:32"
// per un'attivazione delle 10:32 di Roma
const GIORNO = { day: '2-digit', month: '2-digit', year: 'numeric' };
const ORA = { hour: '2-digit', minute: '2-digit' };
export const giornoEOraRoma = (valore) => `${formattaRoma(valore, GIORNO)} ${formattaRoma(valore, ORA)}`;

// "Attivato il 15/09/2026 alle 10:32": due dispositivi con lo stesso nome ("iPhone · Safari") si distinguono.
// `lingua` ultimo parametro, italiano di base (la data resta in cifre: è uguale in tutte le lingue).
export const attivatoIl = (creato, lingua = PRINCIPALE) => {
  const giorno = formattaRoma(creato, GIORNO);
  const { t } = contesto(lingua);
  return giorno ? t('profilo.dispositivi.attivato_il', { giorno, ora: formattaRoma(creato, ORA) }) : t('profilo.dispositivi.attivato');
};

// Il nome del modo di entrare (piattaforma().sistema) come chiave del dizionario
const CHIAVE_SISTEMA = { 'Face ID': 'face_id', 'Touch ID': 'touch_id', 'Windows Hello': 'windows_hello', impronta: 'impronta', 'Face ID / impronta': 'face_id_impronta' };

// Profilo: dispositivi da cui si entra con Face ID / impronta (passkey), con "Rimuovi" (dopo una conferma)
const DispositiviAccessoRapido = () => {
  const { t, l: lingua } = useTraduzioni(); // lo staff non passa da FornitoreLingua: resta in italiano
  const { user } = useAuth();
  const [elenco, setElenco] = useState(null);
  const [errore, setErrore] = useState(false); // elenco non arrivato: lo si dice (prima sembrava "Nessun dispositivo")
  const [supportate, setSupportate] = useState(false);
  const [questo, setQuesto] = useState(dispositivoConPasskey);
  const [inCorso, setInCorso] = useState(null); // id in rimozione, oppure 'attiva'
  const [daConfermare, setDaConfermare] = useState(null); // id del dispositivo di cui si chiede la conferma
  const opzioni = useRef(null);
  const { sistema, dispositivo } = piattaforma();
  const con = t('profilo.dispositivi.con', { sistema: t(`profilo.dispositivi.sistemi.${CHIAVE_SISTEMA[sistema] || 'face_id_impronta'}`) });

  const carica = useCallback(async () => {
    setErrore(false);
    try {
      const { data } = await axios.get(`${API}/auth/passkey`);
      setElenco(Array.isArray(data) ? data : []);
    } catch {
      setErrore(true);
    }
  }, []);

  // Questo dispositivo è attivo solo se il segno locale è dell'utente entrato ed è ancora nell'elenco
  const email = (user?.email || '').toLowerCase();
  const questoAttivo = !!questo && questo.email === email
    && (!questo.id || !elenco || elenco.some((p) => p.id === questo.id));

  useEffect(() => {
    carica();
    passkeySupportate().then(setSupportate);
  }, [carica]);

  useEffect(() => {
    // opzioni pronte prima del tocco su "Attiva" (Safari lo richiede)
    if (supportate && !questoAttivo) preparaRegistrazione().then((o) => { opzioni.current = o; }).catch(() => {});
  }, [supportate, questoAttivo]);

  const attiva = async () => {
    setInCorso('attiva');
    try {
      await registraPasskey(opzioni.current, email);
      setQuesto(dispositivoConPasskey());
      toast.success(t('profilo.dispositivi.attivo'));
      await carica();
    } catch (err) {
      opzioni.current = null;
      if (err?.name === 'InvalidStateError') toast.info(t('profilo.dispositivi.gia_registrato'));
      else if (!annullato(err)) toast.error(contesto(lingua).errore(err?.response?.data?.detail) || t('profilo.dispositivi.attivazione_no'));
      preparaRegistrazione().then((o) => { opzioni.current = o; }).catch(() => {});
    } finally {
      setInCorso(null);
    }
  };

  const rimuovi = async (passkey) => {
    setDaConfermare(null);
    setInCorso(passkey.id);
    const dimentica = () => {
      if (questo?.id === passkey.id) {
        dimenticaDispositivo();
        setQuesto(null);
      }
    };
    try {
      await axios.delete(`${API}/auth/passkey/${passkey.id}`);
      dimentica();
      toast.success(t('profilo.dispositivi.rimosso', { nome: passkey.nome_dispositivo }));
      await carica();
    } catch (err) {
      const stato = err?.response?.status;
      if (stato >= 400 && stato < 500 && stato !== 401) {
        // già tolto da un'altra scheda o da un altro dispositivo: prima «Dispositivo non trovato», la riga restava e il
        // segno di questo dispositivo (mk_passkey) restava nel browser. Si rilegge l'elenco e lo si dice.
        const elencoNuovo = await axios.get(`${API}/auth/passkey`).then((r) => (Array.isArray(r.data) ? r.data : null)).catch(() => null);
        if (elencoNuovo) setElenco(elencoNuovo);
        if (stato === 404 || (elencoNuovo && !elencoNuovo.some((p) => p.id === passkey.id))) {
          dimentica();
          toast.info(t('profilo.dispositivi.gia_rimosso', { nome: passkey.nome_dispositivo }));
        } else {
          const dettaglio = typeof err.response.data?.detail === 'string'
            ? contesto(lingua).errore(err.response.data.detail) : t('profilo.dispositivi.rimozione_no');
          toast.error(t('profilo.dispositivi.rimozione_no_elenco', { messaggio: dettaglio }));
        }
      } else {
        toast.error(stato >= 500 || !err?.response ? t('profilo.dispositivi.rimozione_servizio')
          : contesto(lingua).errore(err?.response?.data?.detail) || t('profilo.dispositivi.rimozione_no'));
      }
    } finally {
      setInCorso(null);
    }
  };

  return (
    <Card className="card-luxury" data-testid="dispositivi-accesso-rapido">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-gold text-lg flex items-center gap-2">
          <Fingerprint className="h-5 w-5" /> {t('profilo.dispositivi.titolo')}
        </CardTitle>
        <CardDescription className="text-white/50 text-sm">
          {t('profilo.dispositivi.spiega')}
        </CardDescription>
      </CardHeader>
      {/* passkey-*: classi per lo stile dedicato del profilo del cliente (profilo.css), dove le classi Tailwind
          degli spazi interni sono annullate da .mk-home */}
      <CardContent className="space-y-2 passkey-elenco">
        {errore && (
          <p className="text-sm text-red-300 passkey-errore" role="alert">
            {t('profilo.dispositivi.non_letti')}{' '}
            <button type="button" className="underline passkey-riprova" onClick={carica}>{t('comune.riprova')}</button>
          </p>
        )}
        {!errore && elenco === null && <p className="text-sm text-white/40">{t('comune.caricamento')}</p>}
        {!errore && elenco?.length === 0 && (
          <p className="text-sm text-white/50">
            {t('profilo.dispositivi.nessuno')}
          </p>
        )}
        {!errore && elenco?.map((p) => (
          <div key={p.id} className="rounded-lg border border-gold/15 bg-white/5 px-3 py-2.5 passkey-riga" data-testid="passkey-dispositivo">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <Smartphone className="h-4 w-4 text-gold/70 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {p.nome_dispositivo}
                    {questo?.id === p.id && <Badge variant="outline" className="ml-2 border-gold/40 text-gold text-[10px]">{t('profilo.dispositivi.questo')}</Badge>}
                  </p>
                  <p className="text-xs text-white/40">
                    {attivatoIl(p.creato, lingua)} · {p.ultimo_uso
                      ? t('profilo.dispositivi.ultimo_accesso', { quando: giornoEOraRoma(p.ultimo_uso) }) : t('profilo.dispositivi.mai_usato')}
                  </p>
                </div>
              </div>
              {daConfermare !== p.id && (
                <Button variant="outline" size="sm" className="min-h-[44px] border-red-500/30 text-red-400 hover:bg-red-500/10 shrink-0"
                  disabled={inCorso === p.id} onClick={() => setDaConfermare(p.id)} data-testid="passkey-rimuovi">
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {t(inCorso === p.id ? 'profilo.dispositivi.rimozione' : 'profilo.dispositivi.rimuovi')}
                </Button>
              )}
            </div>
            {daConfermare === p.id && (
              <div className="mt-2.5 border-t border-gold/10 pt-2.5 passkey-conferma" role="group" aria-label={t('profilo.dispositivi.conferma_aria', { nome: p.nome_dispositivo })}>
                <p className="text-sm text-white/80">
                  {t(questo?.id === p.id ? 'profilo.dispositivi.conferma_questo' : 'profilo.dispositivi.conferma_altro',
                    { nome: p.nome_dispositivo, quando: attivatoIl(p.creato, lingua).toLowerCase() })}
                </p>
                <div className="mt-2 flex flex-wrap gap-2 passkey-bottoni">
                  <Button variant="outline" size="sm" className="min-h-[44px] border-red-500/40 text-red-400 hover:bg-red-500/10"
                    onClick={() => rimuovi(p)} data-testid="passkey-conferma-rimozione">
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {t('profilo.dispositivi.si_rimuovi')}
                  </Button>
                  <Button variant="ghost" size="sm" className="min-h-[44px] text-white/70"
                    onClick={() => setDaConfermare(null)} data-testid="passkey-annulla-rimozione">
                    {t('comune.annulla')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
        {supportate && !questoAttivo && (
          <Button className="btn-gold w-full min-h-[44px] mt-2" disabled={inCorso === 'attiva'} onClick={attiva} data-testid="passkey-attiva-profilo">
            <Fingerprint className="h-4 w-4 mr-2" />
            {inCorso === 'attiva' ? t('profilo.dispositivi.attivazione') : t('profilo.dispositivi.attiva', { con, dispositivo })}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export { DispositiviAccessoRapido };
